import React, { useEffect, useMemo, useState, useCallback } from 'react'
import i18n from 'constants/strings'
import { Country, useCountries } from 'hooks/use-countries'
import CheckboxInput from 'components/generic/CheckBoxInput'
import Dropdown from 'components/generic/Dropdown'
import cx from 'classnames'
import styles from './styles/dropdownCountries.module.scss'
import FilterField from 'components/generic/FilterField'
import useKeyboardNavigation from 'hooks/use-keyboard-navigation'

interface Props {
  defaultLabel: string
  setCountries: (codesArray?: string[]) => void
  filterCountries?: string[]
}

const DropdownCountries: React.FC<Props> = ({ defaultLabel, setCountries, filterCountries }) => {
  const [inputFilter, setInputFilter] = useState<string>('')
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const { activeCountriesList, getCountryName } = useCountries()
  const filteredCountries = useMemo(() => {
    return inputFilter
      ? activeCountriesList?.filter(
          (c: Country) =>
            c && c.name.normalize().toLowerCase().startsWith(inputFilter.toLowerCase().normalize())
        )
      : [...activeCountriesList]
  }, [inputFilter, activeCountriesList])

  const toggleSelection = useCallback(
    (country: Country) => {
      if (selectedCountries.includes(country['alpha-3'])) {
        setSelectedCountries(selectedCountries.filter(el => el !== country['alpha-3']))
      } else {
        setSelectedCountries([...selectedCountries, country['alpha-3']])
      }
    },
    [selectedCountries]
  )

  const { activeIndex, setActiveIndex, isListContainerFocused, listContainerProps } =
    useKeyboardNavigation<HTMLDivElement, Country>({
      listItems: filteredCountries,
      handleSelection: toggleSelection,
    })

  const handleClear = () => {
    setCountries(undefined)
    setInputFilter('')
    setIsOpen(false)
  }

  const handleCancel = () => {
    setSelectedCountries(filterCountries ?? [])
    setInputFilter('')
    setIsOpen(false)
  }

  const handleConfirm = () => {
    setCountries(selectedCountries)
    setInputFilter('')
    setIsOpen(false)
  }

  const dropdownLabel = !filterCountries?.length
    ? defaultLabel
    : filterCountries.length > 1
    ? i18n.t('strFilterBar.nationalityCompositeDropdownLabel', {
        numberSelected: filterCountries.length,
      })
    : getCountryName(filterCountries[0])

  useEffect(() => {
    setActiveIndex(0)
  }, [inputFilter, setActiveIndex])

  useEffect(() => {
    setSelectedCountries(filterCountries ?? [])
    return () => {
      setSelectedCountries(filterCountries ?? [])
    }
  }, [setSelectedCountries, filterCountries])

  return (
    <Dropdown
      showClearBtn={!!filterCountries}
      contentTrigger={dropdownLabel}
      controls={{ isOpen, setIsOpen }}
      withChevron
      cancelBtn={{
        content: i18n.t('strCommonUI.button.cancel'),
        onClick: handleCancel,
      }}
      confirmBtn={{
        content: i18n.t('strCommonUI.button.done'),
        onClick: handleConfirm,
      }}
      onClear={handleClear}
      classNames={{
        contentWrapper: styles.contentWrapper,
        btnsContainer: styles.btnsContainer,
        trigger: { [styles.trigger]: true, '--is-active': !!filterCountries },
      }}
    >
      <FilterField
        value={inputFilter}
        onValueChange={setInputFilter}
        onClear={() => setInputFilter('')}
        inputProps={{
          placeholder: i18n.t('strFilterBar.countryFilterPlaceholder'),
        }}
        classNames={{ wrapper: styles.filterInput }}
      />
      <div
        {...listContainerProps}
        id='countries-list'
        role='listbox'
        className={cx(styles.checkboxGroupContainer, {
          '--is-focused': isListContainerFocused,
        })}
        tabIndex={0}
        aria-activedescendant={filteredCountries[activeIndex]?.name}
      >
        {filteredCountries?.map((country, i) => (
          <CheckboxInput
            key={country['alpha-3']}
            className={i === activeIndex && isListContainerFocused ? '--is-focused' : undefined}
            onClick={() => {
              toggleSelection(country)
              setActiveIndex(i)
            }}
            label={country.name}
            isChecked={selectedCountries.includes(country['alpha-3'])}
            asOption
            ariaSetsize={filteredCountries.length}
            ariaSelected={i === activeIndex}
          />
        ))}
      </div>
    </Dropdown>
  )
}

export default DropdownCountries
